import { queryClient } from 'App';
import debounce from 'lodash/debounce';

import { socket } from 'lib/clients/socket';
import { appointmentKeys } from 'lib/queries/appointmentKeys';
import { COMMUNICATION_LOGS_QUERY_KEY } from 'lib/constants/queries/queryKeys';
import { invalidateAllAppointments } from 'lib/queries/appointmentInvalidation';

// Behavior preserved bit-for-bit from the pre-factory implementation:
//   1. Broad invalidation of every cached appointment query.
//   2. If the payload's `invalidate` flag is falsy, optimistically patch the
//      `appointmentv2` detail entry so the broadcast wins the race against
//      the in-flight refetch.
// The `invalidate` flag semantics remain undocumented on the backend side
// (plan §6 step 0 / socket section) — the redesign is deferred.
const runAppointmentUpdate = debounce((data) => {
  const { appointment, invalidate } = data;

  invalidateAllAppointments(queryClient);
  if (invalidate) return;

  // detailV2(id) throws on a null id; guard (debounced timer, no upstream catch).
  if (appointment?.id == null) return;

  queryClient.setQueryData(appointmentKeys.detailV2(appointment?.id), (oldData) => {
    if (!oldData) return;
    return {
      ...oldData,
      appointment: { ...(oldData?.appointment || {}), ...appointment }
    };
  });
}, 300);

export default (data) => {
  const { appointment, from } = data ?? {};

  // Communication logs refresh on every echo, including our own.
  queryClient.invalidateQueries({
    queryKey: appointment?.user_id
      ? [COMMUNICATION_LOGS_QUERY_KEY, appointment.user_id]
      : [COMMUNICATION_LOGS_QUERY_KEY],
    exact: false
  });

  // Skip only our OWN appointment refetch — this tab already refreshed via the
  // mutation. Checked before the debounce so a coalesced foreign echo isn't dropped.
  if (from && socket?.id && from === socket.id) return;

  runAppointmentUpdate(data);
};
